import { BehaviorSubject, of as observableOf } from "rxjs";
import { mergeMap, takeUntil } from "rxjs/operators";
import { Component, OnInit, Inject, ChangeDetectorRef } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AuthService } from "app/users";
import { RegisterData, RegisterErrors } from "app/core/register-data";
import { Events, FirebaseEventsService } from "app/common/firebase-events.service";
import { StatementManagementService } from "app/statements/statement-management.service";
import { SubscribingComponent } from "app/common";
import { DeviceDetectorService } from "ngx-device-detector";
import { FirebaseHttpService } from "app/firebase/firebase.http.service";
import { FirebaseNotification } from "app/firebase/notification";
import { getUser, State } from "app/reducers";
import { Store } from "@ngrx/store";
import { User } from "app/users/user";
import { StatementService } from "app/statements/statement.service";
import { ImpressumComponent } from "app/core/impressum/impressum.component";
import { PersonalInfoOnLoginInterface } from "app/users";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { UserChangePasswordHttpService } from "app/modules/user/services/http/user-change-password.http.service";
import { DashboardPagePath } from "app/core/workflow/page-path/dashboard-page.path";
import { CaptchaModule } from "app/shared/modules/captcha/captcha.module";
import { CommonModule } from "@angular/common";
import { FormInputReactiveComponent } from "app/shared/standalone/components/form-input-reactive/form-input-reactive.component";
import { ClickElsewhereDirective } from "app/shared/standalone/directives/click-elsewhere.directive";
import { MoreInfoTooltipComponent } from "app/shared/standalone/components/more-info-tooltip/more-info-tooltip.component";
import { MatIconModule } from "@angular/material/icon";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";
import { FormInputComponent } from "app/shared/standalone/components/form-input/form-input.component";
import { ChooseLanguageComponent } from "app/shared/standalone/components/choose-language/choose-language.component";
import { AnalyticsService } from "app/shared/modules/analytics/services/analytics.service";
import { AnalyticsModule } from "app/shared/modules/analytics/analytics.module";
import { TermsFilesService } from "app/core/services/terms-files.service";
import { CookiesLawService } from "app/core/services/cookies-law.service";
import { CaptchaComponent } from "app/shared/modules/captcha/components/captcha/captcha.component";
import { env } from "process";
import { MobileEnvironmentSwitchHelper } from "app/core/helpers/mobile-environment-switch.helper";
import { PasswordInputComponent } from "../password-input/password-input.component";
import { UserTwoFactorHttpService } from "app/modules/user/services/http/user-two-factor.http.service";
import { OTP_TYPE } from "app/shared/components/2fa-dialog/2fa-set-up-dialog.component";
import { AppsFlyerService } from "app/shared/services/appsflyer/appsflyer.service";
import { PrivacyPolicyComponent } from "app/standalone-pages/privacy-policy/privacy-policy.component";
import { TermsAndConditionsComponent } from "app/standalone-pages/terms-and-conditions/terms-and-conditions.component";
interface LoginErrors {
  email?: string[];
  password?: string[];
  non_field_errors?: string[];
  detail?: string;
}

interface ViewSelection {
  loginFlag: AuthenticationDialogFlag;
}

type CaptchaFlowType = "login" | "forgot" | "register";

export enum AuthenticationDialogFlag {
  LOGIN = "login",
  FORGOT = "forgot",
  FORGOT_SENT = "forgot_sent",
  REGISTRATION = "register",
  TWO_FACTOR_AUTH = "two_factor_auth"
}

@Component({
  selector: "app-login-dialog",
  templateUrl: "./login-dialog.component.html",
  styleUrls: ["./login-dialog.component.scss"],
  imports: [
    CommonModule,
    ClickElsewhereDirective,
    MoreInfoTooltipComponent,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    MatDialogModule,
    CaptchaModule,
    FormInputComponent,
    FormInputReactiveComponent,
    ChooseLanguageComponent,
    AnalyticsModule,
    PasswordInputComponent
  ],
  providers: [StatementManagementService, FirebaseEventsService, DeviceDetectorService, StatementService]
})
export class LoginDialogComponent extends SubscribingComponent implements OnInit {
  private readonly registerBackendErrorTranslations: Record<string, string> = {
    "Email is invalid": "REGISTER.ERRORS.EMAIL_INVALID",
    "This password must contain at least 1 numbers.": "REGISTER.ERRORS.PASSWORD_MUST_CONTAIN_NUMBER",
    "This password must contain at least 1 number.": "REGISTER.ERRORS.PASSWORD_MUST_CONTAIN_NUMBER",
    "This coupon does not exist.": "SNACKBAR.COUPON_DOES_NOT_EXIST",
    "This coupon does not exist": "SNACKBAR.COUPON_DOES_NOT_EXIST"
  };

  public captchaToken$ = new BehaviorSubject<string>(undefined);
  public isLoginCaptchaVisible = false;
  public isForgotCaptchaVisible = false;
  public isRegisterCaptchaVisible = false;
  returnUrl: string;
  submitted: boolean;
  email: string;
  password: string;
  registerData: RegisterData;
  isPasswordValidated = false;
  loginErrors: LoginErrors;
  registerErrors: RegisterErrors;
  areTermsAccepted = false;
  isEmailCodeSend = false;
  public readonly steps = AuthenticationDialogFlag;
  public activeStep: AuthenticationDialogFlag = AuthenticationDialogFlag.LOGIN;
  public showLoginForm = true;
  public personalInfoOnLogin: PersonalInfoOnLoginInterface = {
    firstname: "",
    lastname: "",
    emailAddress: ""
  };
  public forgotPasswordFormEmailError: string[];
  public forgotPasswordForm = new UntypedFormGroup({
    email: new UntypedFormControl({ value: "", disabled: this.isForgotPasswordLoading }, [
      Validators.required,
      Validators.email
    ])
  });
  personalInfoOnRegister: any;
  public set isForgotPasswordLoading(_isForgotPasswordLoading: boolean) {
    this._isForgotPasswordLoading = _isForgotPasswordLoading;
    this._isForgotPasswordLoading
      ? this.forgotPasswordForm.get("email").disable()
      : this.forgotPasswordForm.get("email").enable();
  }
  public get isForgotPasswordLoading(): boolean {
    return this._isForgotPasswordLoading;
  }
  private _isForgotPasswordLoading = false;
  private user: User;
  public positiveRegistration = false;
  private pendingCaptchaFlow: CaptchaFlowType | null = null;

  public resetCaptcha = false;

  public mobileEnvironmentSwitcher: MobileEnvironmentSwitchHelper = new MobileEnvironmentSwitchHelper();
  public twoFactorAuth: string;
  public verifyToken: string;
  public otpError: Array<string> = [];
  public authCodeError: Array<string> = [];
  public otpType: OTP_TYPE;
  public otpTypes = OTP_TYPE;
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private userChangePasswordHttpService: UserChangePasswordHttpService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<LoginDialogComponent>,
    private statementManagementService: StatementManagementService,
    private analyticsService: AnalyticsService,
    private firebaseEventsService: FirebaseEventsService,
    private deviceService: DeviceDetectorService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private store: Store<State>,
    private firebaseHttpService: FirebaseHttpService,
    private statementService: StatementService,
    private termsFilesService: TermsFilesService,
    @Inject(MAT_DIALOG_DATA) private data: ViewSelection,
    private cookiesLawService: CookiesLawService,
    private twoFactorHttpService: UserTwoFactorHttpService
  ) {
    super();
  }

  ngOnInit() {
    this.dialogRef.disableClose = true;
    this.registerData = new RegisterData();

    if (this.data && this.data.loginFlag) {
      this.activeStep = this.data.loginFlag;
      if (sessionStorage.getItem("personalInfoOnLogin")) {
        this.personalInfoOnLogin = JSON.parse(sessionStorage.getItem("personalInfoOnLogin"));

        this.email = this.personalInfoOnLogin.emailAddress;
        this.registerData.email = this.personalInfoOnLogin.emailAddress;
      }
    }
    this.returnUrl = this.route.snapshot.queryParams["returnUrl"] || "/";

    this.registerData.clientType = "individual";
    this.statementManagementService.loginDialogOpened.emit();

    if (window.innerWidth < 768) {
      this.showLoginForm = false;
    }
  }

  captchaResolved(value: string | null): void {
    this.captchaToken$.next(value);

    if (!value || !this.pendingCaptchaFlow) {
      return;
    }

    const captchaFlow = this.pendingCaptchaFlow;
    this.pendingCaptchaFlow = null;

    if (captchaFlow === "login") {
      this.performLogin();
      return;
    }

    if (captchaFlow === "forgot") {
      this.performForgotPasswordRequest();
      return;
    }

    this.performRegister();
  }

  public get isLogin(): boolean {
    return this.activeStep === this.steps.LOGIN;
  }

  public get isForgot(): boolean {
    return this.activeStep === this.steps.FORGOT;
  }

  public get isForgotSent(): boolean {
    return this.activeStep === this.steps.FORGOT_SENT;
  }

  public get isRegistration(): boolean {
    return this.activeStep === this.steps.REGISTRATION;
  }

  public get forgotPasswordFormEmail(): UntypedFormControl {
    return this.forgotPasswordForm.get("email") as UntypedFormControl;
  }

  public async onSubmitForgot(): Promise<void> {
    this.forgotPasswordFormEmailErrorCheck();

    if (!this.forgotPasswordForm.valid || this.isForgotPasswordLoading) {
      return;
    }

    if (this.ensureCaptchaAndQueueSubmit("forgot")) {
      return;
    }

    await this.performForgotPasswordRequest();
  }

  public executeFirebaseEvent = (event: string, params: any) => this.firebaseEventsService.logEvent(event, params);

  login() {
    if (this.isLoginDisabled) {
      return;
    }

    if (this.ensureCaptchaAndQueueSubmit("login")) {
      return;
    }

    this.performLogin();
  }

  public loginWithTwoFactor() {
    this.submitted = true;

    this.email = this.email.toLocaleLowerCase();

    if (sessionStorage.getItem("personalInfoOnLogin")) {
      this.personalInfoOnLogin = JSON.parse(sessionStorage.getItem("personalInfoOnLogin"));
    }
    this.personalInfoOnLogin.emailAddress = this.email;
    sessionStorage.setItem("personalInfoOnLogin", JSON.stringify(this.personalInfoOnLogin));

    this.authService
      .loginWith2FA(this.email, this.password, this.verifyToken, this.twoFactorAuth)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.firebaseEventsService.logEvent("login with two factor code", null);
          this.closeDialog();
        },
        error: (error: any) => {
          this.submitted = false;
          console.error(error);
          if (error?.error?.otp) {
            this.otpError = [error.error.otp];
          } else if (error?.error?.message) {
            this.otpError = [error.error.message];
          } else if (error?.error?.verify_token) {
            this.otpError = [error.error.verify_token];
          } else {
            this.otpError = [this.translate.instant("TWO_FACTOR_MODAL.ERROR")];
          }
        }
      });
  }

  public handleResendToFactoreCode(): void {
    this.twoFactorAuth = "";
    this.twoFactorHttpService.resendTwoFactorCodeToEmail(this.verifyToken).subscribe({
      next: (response) => {
        this.isEmailCodeSend = true;
      },
      error: (error) => {
        console.error(error);
        this.isEmailCodeSend = false;

        if (error.error.message) {
          this.otpError = [error.error.message];
        } else {
          this.otpError = [this.translate.instant("TWO_FACTOR_MODAL.ERROR")];
        }
      }
    });
  }

  public handleAuthCodeValue(value: string): void {
    if (this.isEmailCodeSend) this.isEmailCodeSend = false;
    this.twoFactorAuth = value;
  }

  register() {
    if (this.isRegisterDisabled()) {
      return;
    }

    if (this.ensureCaptchaAndQueueSubmit("register")) {
      return;
    }

    this.performRegister();
  }

  public showTermsAndConditionsDialog(event) {
    this.dialog.open(TermsAndConditionsComponent, { autoFocus: false });
  }

  public showCookieBanner(event): void {
    event.preventDefault();
    this.dialogRef.close();
    this.cookiesLawService.activeCookieBanner();
  }

  public showPrivacyPolicyDialog() {
    this.dialog.open(PrivacyPolicyComponent, { autoFocus: false });
  }

  public showImpressumDialog() {
    this.dialog.open(ImpressumComponent);
  }

  public showLoginBox() {
    this.activeStep = this.steps.LOGIN;
    this.showLoginForm = true;
    this.enableEditing();
    this.resetCaptchaState();
    this.getUserDataAfterRegister();
  }

  public showSignInBox() {
    this.activeStep = this.steps.REGISTRATION;
    this.showLoginForm = true;
    this.enableEditing();
    this.resetCaptchaState();
  }

  public forgotPassword(): void {
    this.activeStep = this.steps.FORGOT;
    this.showLoginForm = true;
    this.enableEditing();
    this.resetCaptchaState();
  }

  getUserDataAfterRegister() {
    if (sessionStorage.getItem("personalInfoOnRegister")) {
      this.personalInfoOnRegister = JSON.parse(sessionStorage.getItem("personalInfoOnRegister"));
      this.email = this.personalInfoOnRegister.email;
      this.password = this.personalInfoOnRegister.password;
      sessionStorage.removeItem("personalInfoOnRegister");

      this.positiveRegistration = true;
    }
  }

  closeDialog(register = false): void {
    this.store
      .select(getUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user: User) => {
        if (user && user.id) {
          this.user = user;
          if (!user.isBusiness && !register) {
            this.statementService
              .getAllOpenedStatement()
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe({
                next: (response: any): void => {
                  let numberOfOpenedStatement = 0;
                  if (response.statements_count) {
                    numberOfOpenedStatement = Number(response.statements_count);
                  }
                  if (numberOfOpenedStatement > 0) {
                    this.router.navigate([DashboardPagePath.fullUrl()]);
                    this.dialogRef.close();
                  } else {
                    this.getStatement();
                  }
                },
                error: () => {
                  this.getStatement();
                }
              });
          } else if (register) {
            this.getStatement();
          } else {
            this.firebaseNtificationOnLoginOrRegister();
            this.dialogRef.close();
          }
        }
      });
  }

  firebaseNtificationOnLoginOrRegister(): void {
    if (!this.user || !this.user.id) {
      return;
    }

    const notification = new FirebaseNotification();
    notification.user = this.user.id;
    notification.statement = 0;
    notification.notificationName = "login_or_register";
    notification.eventOneTriggered = true;
    notification.notificationSent = false;

    this.firebaseHttpService
      .udpateFirebaseNotification(notification)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response) => {},
        (error) => {
          console.log(error);
        }
      );
  }

  private performLogin(): void {
    if (this.submitted) {
      return;
    }

    this.submitted = true;
    this.email = this.email.toLocaleLowerCase();

    if (sessionStorage.getItem("personalInfoOnLogin")) {
      this.personalInfoOnLogin = JSON.parse(sessionStorage.getItem("personalInfoOnLogin"));
    }
    this.personalInfoOnLogin.emailAddress = this.email;
    sessionStorage.setItem("personalInfoOnLogin", JSON.stringify(this.personalInfoOnLogin));

    this.authService
      .login(this.email, this.password, this.captchaToken$.getValue())
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          if (res.verify_token) {
            this.submitted = false;
            this.verifyToken = res.verify_token;
            this.otpType = res.otp_type;
            this.activeStep = this.steps.TWO_FACTOR_AUTH;
            if (res.otp_send_failed) {
              this.otpError = [res.message ?? this.translate.instant("TWO_FACTOR_MODAL.ERROR")];
            }
          } else {
            this.firebaseEventsService.logEvent("login", null);
            this.closeDialog();
          }
        },
        error: (error: any) => {
          this.submitted = false;
          this.loginErrors = error.error;
          this.clearLoginPassword();
          this.resetCaptcha = !this.resetCaptcha;
        }
      });
  }

  private async performForgotPasswordRequest(): Promise<void> {
    this.executeFirebaseEvent("reset_password", null);

    this.isForgotPasswordLoading = true;
    const response = await this.userChangePasswordHttpService.resetPasswordRequest(
      this.forgotPasswordFormEmail.value,
      this.captchaToken$.getValue()
    );
    this.isForgotPasswordLoading = false;

    if (response.success) {
      this.activeStep = this.steps.FORGOT_SENT;
      return;
    }

    if (response.error?.email) {
      this.forgotPasswordFormEmailError = response.error?.email;
    } else {
      this.forgotPasswordFormEmailError = [this.translate.instant("COMMON.GLOBAL_ERROR")];
    }
  }

  private performRegister(): void {
    if (this.submitted) {
      return;
    }

    this.submitted = true;
    this.registerData.language = this.translate.currentLang;
    this.registerData.email = this.registerData.email.toLocaleLowerCase();

    if (sessionStorage.getItem("personalInfoOnLogin")) {
      this.personalInfoOnLogin = JSON.parse(sessionStorage.getItem("personalInfoOnLogin"));
    }
    this.personalInfoOnLogin.emailAddress = this.registerData.email;
    sessionStorage.setItem("personalInfoOnLogin", JSON.stringify(this.personalInfoOnLogin));
    sessionStorage.removeItem("personalInfoOnRegister");

    this.authService
      .register(this.registerData, this.captchaToken$.getValue())
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          sessionStorage.setItem(
            "personalInfoOnRegister",
            JSON.stringify({ email: this.registerData.email, password: this.registerData.password })
          );
          this.firebaseEventsService.logEvent(Events.sign_up, null);
          this.firebaseEventsService.webEvent(Events.sign_up, null);
          this.analyticsService.logSignUpEvent();
          AppsFlyerService.logEvent("account_registration");
          this.showLoginBox();
        },
        error: (error: any) => {
          this.submitted = false;
          this.registerErrors = this.mapRegisterErrors(error && error.error ? error.error : null);
          sessionStorage.removeItem("personalInfoOnRegister");
          this.clearRegisterPassword();
          this.captchaToken$.next(undefined);
          this.resetCaptcha = !this.resetCaptcha;
        }
      });
  }

  private ensureCaptchaAndQueueSubmit(flow: CaptchaFlowType): boolean {
    if (this.captchaToken$.getValue()) {
      return false;
    }

    this.pendingCaptchaFlow = flow;
    this.isLoginCaptchaVisible = flow === "login";
    this.isForgotCaptchaVisible = flow === "forgot";
    this.isRegisterCaptchaVisible = flow === "register";
    return true;
  }

  private resetCaptchaState(): void {
    this.pendingCaptchaFlow = null;
    this.isLoginCaptchaVisible = false;
    this.isForgotCaptchaVisible = false;
    this.isRegisterCaptchaVisible = false;
    this.captchaToken$.next(undefined);
  }

  enableEditing() {
    this.submitted = false;
    this.loginErrors = {};
    this.registerErrors = {};
  }

  public get isLoginDisabled(): boolean {
    return this.submitted || !this.password || !this.email;
  }

  isRegisterDisabled() {
    return this.isRegisterFormInvalid() || !this.areTermsAccepted || this.submitted;
  }

  private getStatement() {
    if (this.statementService.isExistsInLocalStorage()) {
      this.firebaseNtificationOnLoginOrRegister();
      this.dialogRef.close({
        next: this.user && !this.user.isBusiness
      });

      return;
    }

    this.firebaseNtificationOnLoginOrRegister();
    this.dialogRef.close();
    this.router.navigate([`/new/`]);
  }

  private isRegisterFormInvalid() {
    return !this.registerData.email || !this.isPasswordValidated;
  }

  private clearLoginPassword() {
    this.password = "";
  }

  private clearRegisterPassword(): void {
    this.registerData.password = "";
    this.registerData.confirmPassword = "";
  }

  private forgotPasswordFormEmailErrorCheck(): void {
    if (this.forgotPasswordFormEmail.invalid) {
      if (this.forgotPasswordFormEmail.errors?.["required"]) {
        this.forgotPasswordFormEmailError = [this.translate.instant("EP_VALIDATOR.FIELD_IS_REQUIRED")];
        return;
      }
      if (this.forgotPasswordFormEmail.errors?.["email"]) {
        this.forgotPasswordFormEmailError = [this.translate.instant("EP_VALIDATOR.FIELD_WRONG_FORMAT")];
        return;
      }
    }

    this.forgotPasswordFormEmailError = [];
  }

  private mapRegisterErrors(registerErrors: RegisterErrors | null): RegisterErrors | null {
    if (!registerErrors) {
      return null;
    }

    return {
      ...registerErrors,
      non_field_errors: this.translateRegisterErrorMessages(registerErrors.non_field_errors),
      referal_code: this.translateRegisterErrorMessages(registerErrors.referal_code),
      user: registerErrors.user
        ? {
            ...registerErrors.user,
            email: this.translateRegisterErrorMessages(registerErrors.user.email),
            password: this.translateRegisterErrorMessages(registerErrors.user.password),
            confirm_password: this.translateRegisterErrorMessages(registerErrors.user.confirm_password),
            non_field_errors: this.translateRegisterErrorMessages(registerErrors.user.non_field_errors),
            referal_code: this.translateRegisterErrorMessages(registerErrors.user.referal_code)
          }
        : registerErrors.user
    };
  }

  private translateRegisterErrorMessages(messages?: string[]): string[] | undefined {
    return messages?.map((message) => {
      const translationKey = this.registerBackendErrorTranslations[message];
      return translationKey ? this.translate.instant(translationKey) : message;
    });
  }
}
